import logoSources from "data/gdn-logo-sources.json";
import logoIcons from "data/gdn-logo-icons.json";
import logoLockups from "data/gdn-logo-lockups.json";
import {getLogoUrl, getLogoCycle, isBlackLogoUrl} from "libs/logo";

/**
 * GDN logo resolution — Social's per-product logo, served from v3.
 *
 * The Ads "Google Display Network" template must show the same per-product /
 * product-group lockup Social shows (JETBRAINS, JETBRAINS IDEs, JetBrains AI,
 * dotNET, Data, YouTrack, …), picked by the same logoFamily → bg-marker-variant
 * rules. Rather than duplicate any of that, we call Social's getLogoUrl() as-is
 * and only rewrite the resolved asset URL from Kraken/CloudFront to its v3 copy
 * (mirrored by scripts/upload-gdn-logo-sources.mjs). One source of truth for
 * both the grouping (product.logoFamily) and the variant logic (resolveVariant).
 *
 * Manifest maps CloudFront basename → v3 url. A logo absent from the manifest
 * (e.g. a dead CloudFront reference) falls back to the original url rather than
 * rendering a broken <img>.
 */
const V3_LOGO_BY_FILENAME = logoSources as Record<string, string>;

export type GdnLogoColor = "black" | "white";
type LockupVariants = Record<GdnLogoColor, string>;

/** Store the same color hint that the native logo cycle uses. */
export function getGdnColorwayVariant(productValue: string, type: string, layout: number, color: GdnLogoColor): string {
  const value = getLogoCycle(productValue, type, layout, "ads").find((url: string | null) =>
    typeof url === "string" && isBlackLogoUrl(url) === (color === "black"));
  if (!value) throw new Error(`Missing GDN ${color} logo variant for ${productValue}`);
  return value;
}

const V3_LOCKUP_BY_PRODUCT = logoLockups as Record<string, LockupVariants>;
const FIXED_BRAND_ASSET_PRODUCTS = new Set(["DBAO"]);

// Databao has one official brand treatment: a violet symbol with black text.
// It has no white logo variant, so GDN must keep this treatment on its canvas.
const DEFAULT_GDN_LOGO_COLORS: Readonly<Partial<Record<string, GdnLogoColor>>> = {
  DBAO: "black",
};

export function hasGdnFixedBrandAssets(productValue: string): boolean {
  return FIXED_BRAND_ASSET_PRODUCTS.has(productValue);
}

export function getGdnDefaultLogoColor(productValue: string): GdnLogoColor | null {
  return DEFAULT_GDN_LOGO_COLORS[productValue] ?? null;
}

/** Rewrite a `${NEUROBAKER}/Logos/<file>.svg` url to its v3-hosted copy. */
export function toV3LogoUrl(logoUrl: string): string {
  const filename = logoUrl.split("/Logos/")[1];
  return (filename && V3_LOGO_BY_FILENAME[filename]) || logoUrl;
}

/** Social's per-product logo for `productValue` + background markers, served from v3. */
export function getGdnLogoUrl(productValue: string, bgMarkers: readonly string[]): string {
  // "ads", not "social": Social shows IDEs without their lockup since
  // 2026-10-23, and the GDN banners keep it.
  return toV3LogoUrl(getLogoUrl(productValue, bgMarkers, "general", "ads"));
}

// --- Icon-only + stacked logo treatments (Leaderboards, GDN Japan, Half-page,
// Skyscraper). These templates show the product's brand ICON rather than the
// full horizontal wordmark lockup.

const V3_ICON_BY_PRODUCT = logoIcons as Record<string, string>;
// These products use the JetBrains master icon in the Figma logo library, or
// have no dedicated icon there. Every other GDN product must have an explicit
// manifest entry. Do not silently replace missing product artwork with the
// master icon.
const MASTER_ICON_PRODUCTS = new Set([
  "JB",
  "JBD",
  "GDV",
  "IDEs",
  "CPPT",
  "DN",
  "IS",
  "AI",
  "ST",
  "MP",
  "UNI",
]);
const MASTER_ICON = V3_LOGO_BY_FILENAME["JETBRAINS_icon.svg"];
const MASTER_ICON_WHITE = V3_LOGO_BY_FILENAME["JETBRAINS_white_icon_white.svg"];
const MASTER_ICON_BLACK = V3_LOGO_BY_FILENAME["JETBRAINS _black_logo_minimal.svg"];
const MONOCHROME_ICON_PRODUCTS = new Set(["AI"]);
const PRESERVE_ICON_COLOR_PRODUCTS = new Set(["AI", "DBAO"]);

export function usesGdnMasterLogoIcon(productValue: string): boolean {
  return MASTER_ICON_PRODUCTS.has(productValue);
}

export function shouldPreserveGdnIconColor(productValue: string): boolean {
  return PRESERVE_ICON_COLOR_PRODUCTS.has(productValue);
}

/** Monochrome JetBrains icon used by Figma's generic horizontal lockup. */
export function getGdnMasterLogoIcon(
  bgMarkers: readonly string[],
  colorOverride?: GdnLogoColor
): string {
  const useBlack =
    colorOverride === "black" ||
    (!colorOverride &&
      (bgMarkers.includes("blacklogo") || bgMarkers.includes("blacklogoinverted")));
  return useBlack ? MASTER_ICON_BLACK : MASTER_ICON_WHITE;
}

/** Per-product brand icon (v3), with background-aware monochrome fallbacks. */
export function getGdnLogoIcon(
  productValue: string,
  bgMarkers: readonly string[],
  colorOverride?: GdnLogoColor
): string {
  const productIcon = V3_ICON_BY_PRODUCT[productValue];
  if (productIcon) return productIcon;
  if (!usesGdnMasterLogoIcon(productValue)) {
    throw new Error(
      `GDN icon missing for ${productValue}. Add its v3 asset to gdn-logo-icons.json.`
    );
  }
  if (!MONOCHROME_ICON_PRODUCTS.has(productValue)) return MASTER_ICON;

  const resolvedColor = colorOverride ?? getGdnDefaultLogoColor(productValue);
  const useBlack =
    resolvedColor === "black" ||
    (!resolvedColor &&
      (bgMarkers.includes("blacklogo") || bgMarkers.includes("blacklogoinverted")));
  return useBlack ? MASTER_ICON_BLACK : MASTER_ICON_WHITE;
}

// --- Full stacked product lockups (Half-page, Wide skyscraper, Leaderboard
// 970). These templates show the complete designed lockup — colored brand icon
// + product wordmark + "JETBRAINS <family>" — as a single SVG, instead of
// composing an icon + editable name + family label at runtime. Baked from the
// Figma "Logos" file by scripts/upload-gdn-logo-lockups.mjs, one black + one
// white (wordmark recolored) variant per product.

// Products absent from the Figma library are baked as the JetBrains master
// lockup, but keep a runtime fallback so an unknown product can't render a
// broken <img>. JB is always present in the manifest.
const MASTER_LOCKUP = V3_LOCKUP_BY_PRODUCT.JB;
if (!MASTER_LOCKUP) {
  throw new Error(
    "gdn-logo-lockups.json is missing the JB master lockup — run " +
      "scripts/upload-gdn-logo-lockups.mjs to populate it."
  );
}

/**
 * Full stacked product lockup (v3-served). Picks the black-wordmark variant on
 * light backgrounds (bg url carries the `_blacklogo` marker) and the
 * white-wordmark variant on the dark GDN backgrounds (the default) — mirroring
 * the `_blacklogo` logic in resolveVariant (libs/logo.js).
 */
export function getGdnLogoLockup(
  productValue: string,
  bgMarkers: readonly string[],
  colorOverride?: GdnLogoColor
): string {
  const entry = V3_LOCKUP_BY_PRODUCT[productValue] || MASTER_LOCKUP;
  const resolvedColor = colorOverride ?? getGdnDefaultLogoColor(productValue);
  if (resolvedColor) return entry[resolvedColor];
  return bgMarkers.includes("blacklogo") ? entry.black : entry.white;
}
